import React, { useCallback, useEffect, useState } from 'react';
import {
    ProviderClaimTransmission,
} from '../../../..';
import { ITransmissionDetailProps } from './transmission-detail.types';
import { ITransmissionCmsState, TransmissionEmbeddedPanel } from './transmission-detail-embedded';
import { getTransmissionCobMarkers, TimelineCobMarkers } from '../timeline-cob-markers';

export interface ITransmissionWithPaymentIds {
    paymentIds?: string[];
    statuses?: Array<{ status?: string }>;
}

export const TransmissionStatus: React.FunctionComponent<{
    transmission: ITransmissionWithPaymentIds;
}> = ({ transmission }) => <TimelineCobMarkers {...getTransmissionCobMarkers(transmission)} />;

export const TransmissionDetail: React.FunctionComponent<ITransmissionDetailProps> = (props) => {
    const {
        transmission,
        appType,
        previousTransmission,
        downloadDocument,
        viewClaimDocument,
        getPayerSubscriber,
        embedded = false,
    } = props;

    const showDiff = !!previousTransmission?.x12 && !!transmission.x12;

    const [payer, setPayer] = useState<any>();
    const [subscriber, setSubscriber] = useState<any>();
    const [documentUrl, setDocumentUrl] = useState<{ url?: string } | undefined>();
    const [loadingPayer, setLoadingPayer] = useState(false);
    const [documentLoading, setDocumentLoading] = useState(false);

    const documentId = transmission.documentId;

    // Fetched only when the user asks; a failure is shown as unknown, never as "no form" (NS-3).
    const [cmsState, setCmsState] = useState<ITransmissionCmsState>({ status: 'idle' });

    useEffect(() => {
        setCmsState({ status: 'idle' });
    }, [transmission.id]);

    const handleViewCms1500 = useCallback(async () => {
        if (!viewClaimDocument || !transmission.id) return;
        setCmsState({ status: 'loading' });
        try {
            const res = await viewClaimDocument(transmission.id);
            setCmsState(res?.url ? { status: 'loaded', url: res.url } : { status: 'failed' });
        } catch {
            setCmsState({ status: 'failed' });
        }
    }, [viewClaimDocument, transmission.id]);

    useEffect(() => {
        if (appType !== 'provider' || !getPayerSubscriber) return;
        const id = (transmission as ProviderClaimTransmission).payerSubscriberId;
        if (!id) return;
        setLoadingPayer(true);
        getPayerSubscriber(id)
            .then((res) => {
                if (res) {
                    setPayer(res.payer);
                    setSubscriber(res.subscriber);
                }
            })
            .finally(() => setLoadingPayer(false));
    }, [appType, transmission, getPayerSubscriber]);

    useEffect(() => {
        if (!documentId) return;
        setDocumentLoading(true);
        downloadDocument(documentId)
            .then(setDocumentUrl)
            .finally(() => setDocumentLoading(false));
    }, [documentId, downloadDocument]);

    return (
        <TransmissionEmbeddedPanel
            transmission={transmission}
            appType={appType}
            previousTransmission={previousTransmission}
            payer={payer}
            subscriber={subscriber}
            loadingPayer={loadingPayer}
            statuses={transmission.statuses || []}
            showDiff={showDiff}
            documentLoading={documentLoading}
            documentUrl={documentUrl}
            cmsState={viewClaimDocument && transmission.professionalId ? cmsState : undefined}
            onViewCms1500={handleViewCms1500}
        />
    );
};
